import {useTranslation} from 'react-i18next'
import {Button} from '@/components/ui/button'
import {Input} from '@/components/ui/input'
import {Tag} from '@/components/ui/tag'
import {HelpIcon} from '@/components/help-icon'
import {FilterMultiSelect} from '@/components/filter-multi-select'
import {
  CLASIFICACIONES,
  INTERPRETACION_TONE,
  type RiesgoFilaClasificacion,
  type RiesgoFilaInterpretacion,
} from '@/hooks/sgsst/riesgos/use-ipevr'
import {tope} from '@/lib/campos-texto'

const RISK_LEVELS: RiesgoFilaInterpretacion[] = ['I', 'II', 'III', 'IV']
export type EstadoFilter = 'sin_confirmar' | 'confirmada'

type Option = {id: string; name: string}

export function MatrizRiesgosToolbar({
  search,
  onSearch,
  riskFilter,
  onToggleRisk,
  noAceptablePrimero,
  onToggleNoAceptablePrimero,
  canEdit,
  onAddFila,
  onImportar,
  clasificacionFilter,
  onToggleClasificacion,
  estadoFilter,
  onToggleEstado,
  moreFiltersOpen,
  onToggleMoreFilters,
  empleadoOptions,
  empleadoFilter,
  onChangeEmpleadoFilter,
  cargoOptions,
  cargoFilter,
  onChangeCargoFilter,
  areaOptions,
  areaFilter,
  onChangeAreaFilter,
  hasActiveFilters,
  onClearFilters,
}: {
  search: string
  onSearch: (value: string) => void
  riskFilter: Set<RiesgoFilaInterpretacion>
  onToggleRisk: (value: RiesgoFilaInterpretacion) => void
  noAceptablePrimero: boolean
  onToggleNoAceptablePrimero: () => void
  canEdit: boolean
  onAddFila: () => void
  onImportar: () => void
  clasificacionFilter: Set<RiesgoFilaClasificacion>
  onToggleClasificacion: (value: RiesgoFilaClasificacion) => void
  estadoFilter: Set<EstadoFilter>
  onToggleEstado: (value: EstadoFilter) => void
  moreFiltersOpen: boolean
  onToggleMoreFilters: () => void
  empleadoOptions: Option[]
  empleadoFilter: Set<string>
  onChangeEmpleadoFilter: (next: Set<string>) => void
  cargoOptions: Option[]
  cargoFilter: Set<string>
  onChangeCargoFilter: (next: Set<string>) => void
  areaOptions: Option[]
  areaFilter: Set<string>
  onChangeAreaFilter: (next: Set<string>) => void
  hasActiveFilters: boolean
  onClearFilters: () => void
}) {
  const {t} = useTranslation()
  return (
    <div className="surface space-y-4 px-4 py-4">
      <div className="flex flex-wrap items-center gap-2">
        <Input
          maxLength={tope('BUSCA')}
          value={search}
          onChange={(e) => onSearch(e.target.value)}
          placeholder={t('ipevr.search')}
          className="min-w-0 flex-1 sm:max-w-xs"
          data-testid="ipevr-search"
        />
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="inline-flex items-center gap-1 font-mono text-[10px] font-medium tracking-wider text-muted-foreground uppercase">
            {t('ipevr.filter.riskLevel')}
            <HelpIcon
              label={t('common.help.iconLabelFor', {campo: t('ipevr.filter.riskLevel')})}
              testId="ipevr-interpretacion-legend">
              <ul className="space-y-1.5">
                {RISK_LEVELS.map((level) => (
                  <li key={level} className="flex items-start gap-2">
                    <Tag tone={INTERPRETACION_TONE[level]}>{level}</Tag>
                    <span>{t(`ipevr.interpretacionLegend.${level}`)}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-2" data-testid="ipevr-sort-tip">
                {t('ipevr.sort.tip')}
              </p>
            </HelpIcon>
          </span>
          {RISK_LEVELS.map((level) => (
            <button
              key={level}
              type="button"
              onClick={() => onToggleRisk(level)}
              data-testid={`ipevr-filter-${level}`}
              aria-pressed={riskFilter.has(level)}
              className={`rounded-full border px-2.5 py-1 text-xs ${riskFilter.has(level) ? 'border-primary bg-accent text-accent-foreground' : 'border-input text-muted-foreground'}`}>
              {level}
            </button>
          ))}
        </div>
        <Button
          size="sm"
          variant={noAceptablePrimero ? 'default' : 'outline'}
          onClick={onToggleNoAceptablePrimero}
          data-testid="ipevr-no-aceptable-primero">
          {t('ipevr.filter.noAceptablePrimero')}
        </Button>
        {canEdit && (
          <div className="ml-auto flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={onImportar} data-testid="ipevr-importar">
              {t('ipevr.importar.open')}
            </Button>
            <Button size="sm" onClick={onAddFila} data-testid="ipevr-add-fila">
              {t('ipevr.addFila.open')}
            </Button>
          </div>
        )}
      </div>

      <div className="space-y-2 surface-divider border-t border-dashed pt-3">
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="mr-1 font-mono text-[10px] font-medium tracking-wider text-muted-foreground uppercase">
            {t('ipevr.filter.clasificacion')}
          </span>
          {CLASIFICACIONES.map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => onToggleClasificacion(c)}
              data-testid={`ipevr-filter-clasificacion-${c}`}
              aria-pressed={clasificacionFilter.has(c)}
              className={`rounded-full border px-2.5 py-1 text-xs whitespace-nowrap ${clasificacionFilter.has(c) ? 'border-primary bg-accent text-accent-foreground' : 'border-input text-muted-foreground'}`}>
              {t(`ipevr.clasificacion.${c}`)}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="mr-1 font-mono text-[10px] font-medium tracking-wider text-muted-foreground uppercase">
            {t('ipevr.filter.estado')}
          </span>
          {(['sin_confirmar', 'confirmada'] as const).map((estado) => (
            <button
              key={estado}
              type="button"
              onClick={() => onToggleEstado(estado)}
              data-testid={`ipevr-filter-estado-${estado}`}
              aria-pressed={estadoFilter.has(estado)}
              className={`rounded-full border px-2.5 py-1 text-xs whitespace-nowrap ${estadoFilter.has(estado) ? 'border-primary bg-accent text-accent-foreground' : 'border-input text-muted-foreground'}`}>
              {estado === 'sin_confirmar' ? t('ipevr.grid.unconfirmed') : t('ipevr.grid.confirmed')}
            </button>
          ))}
        </div>
      </div>

      <div className="surface-divider border-t border-dashed pt-3">
        <button
          type="button"
          onClick={onToggleMoreFilters}
          aria-expanded={moreFiltersOpen}
          aria-controls="ipevr-more-filters-panel"
          data-testid="ipevr-more-filters-toggle"
          className="flex items-center gap-1.5 font-mono text-[10px] font-medium tracking-wider text-muted-foreground uppercase hover:text-foreground">
          <span aria-hidden="true">{moreFiltersOpen ? '▾' : '▸'}</span>
          {t('ipevr.filter.moreFilters')}
          {empleadoFilter.size + cargoFilter.size + areaFilter.size > 0 && (
            <span className="font-mono text-[10px] text-foreground">
              ({empleadoFilter.size + cargoFilter.size + areaFilter.size})
            </span>
          )}
        </button>
        {moreFiltersOpen && (
          <div id="ipevr-more-filters-panel" className="mt-2 flex flex-wrap items-center gap-1.5">
            <FilterMultiSelect
              label={t('ipevr.filter.empleados')}
              testId="ipevr-filter-empleados"
              options={empleadoOptions}
              selected={empleadoFilter}
              onChange={onChangeEmpleadoFilter}
            />
            <FilterMultiSelect
              label={t('ipevr.filter.cargo')}
              testId="ipevr-filter-cargo"
              options={cargoOptions}
              selected={cargoFilter}
              onChange={onChangeCargoFilter}
            />
            <FilterMultiSelect
              label={t('ipevr.filter.area')}
              testId="ipevr-filter-area"
              options={areaOptions}
              selected={areaFilter}
              onChange={onChangeAreaFilter}
            />
          </div>
        )}
      </div>

      {hasActiveFilters && (
        <div className="flex justify-end surface-divider border-t border-dashed pt-3">
          <Button size="sm" variant="ghost" onClick={onClearFilters} data-testid="ipevr-clear-filters">
            {t('ipevr.filter.clear')}
          </Button>
        </div>
      )}
    </div>
  )
}
